import { useParams, useNavigate } from 'react-router-dom';
import { useState, useCallback } from 'react';
import ZBreadCrumbs from '@/shared/components/custom/breadcrumbs';
import { ROUTES } from '@/shared/constants/routes';
import { useVendorDetail } from './hooks';
import { useGetTeamsListQuery } from '@/modules/auth/requests';
import { VendorDetailSkeleton } from './components/feedback/VendorDetailSkeleton';
import { VendorDetailError } from './components/feedback/VendorDetailError';
import VendorDetailPageContent from './VendorDetailPageContent';
import { AGENT_TYPES, type UploadDialogState } from '@/modules/agent/types';
import UploadDialog from '@/modules/agent/components/upload-dialog';
import type { ProductActionType } from './types/product-actions.types';
import type { VendorProduct } from './types';
import { getAgentSession } from '@/modules/sessions/requests';
import { handleContinueMultiVendorAssessment } from '@/modules/sessions/use-cases/agent-session.use-case';
import type { MultiVendorAgentSession } from '@/modules/sessions/types';
import { generateAndDownloadSessionReport } from '@/modules/agent/utils/downloadReport';
import { addNotification } from '@/shared/states/notification';
import { VersionHistoryDrawer, useVersionHistoryDrawerStore } from '../vendors-v2/components/VersionHistoryDrawer';

/**
 * Helper to determine error type from error object
 */
const getErrorType = (err: Error | null): '404' | '500' | 'network' | 'timeout' => {
  if (!err) return '500';
  const message = err.message.toLowerCase();
  if (message.includes('404') || message.includes('not found')) return '404';
  if (message.includes('network')) return 'network';
  if (message.includes('timeout')) return 'timeout';
  return '500';
};

/**
 * VendorDetailPage - Main entry point for vendor details
 *
 * Fetches vendor data via useVendorDetail hook and renders:
 * - Loading skeleton while fetching
 * - Error state on failure
 * - VendorDetailPageContent on success
 */
export default function VendorDetailPage() {
  const { vendorName } = useParams<{ vendorName: string }>();
  const navigate = useNavigate();
  const decodedVendorName = vendorName ? decodeURIComponent(vendorName) : '';

  // Modal state for New Assessment flow
  const [uploadDialogState, setUploadDialogState] = useState<UploadDialogState>({
    isOpen: false,
  });

  const { data, isLoading, isError, error, refetch } = useVendorDetail(decodedVendorName);

  // Fetch team members for name resolution in products section
  const { data: teamsData } = useGetTeamsListQuery({ isEnabled: true });

  // Version history drawer store
  const openVersionHistoryDrawer = useVersionHistoryDrawerStore((state) => state.openDrawer);

  // Handle opening the New Assessment modal
  const handleNewAssessment = () => {
    setUploadDialogState({
      isOpen: true,
      type: AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT,
      vendorName: data?.vendor_name,
      vendorDomain: data?.primary_domain || undefined,
      vendorLogoUrl: data?.vendor_logo_url || undefined,
    });
  };

  // Handle product actions from kebab menu
  const handleProductAction = useCallback(
    async (action: ProductActionType, product: VendorProduct) => {
      switch (action) {
        case 'view_assessment':
          navigate(`/agent/${product.id}`);
          break;

        case 'download_report':
          try {
            // Fetch session data from API
            const reportResponse = await getAgentSession(product.id);
            const { session: reportSessionData, steps: reportSteps } = reportResponse.data;

            // Load session data into store (required for download to work)
            await handleContinueMultiVendorAssessment({
              session: reportSessionData as MultiVendorAgentSession,
              steps: reportSteps,
            });

            // Generate and download with steps
            await generateAndDownloadSessionReport(product.id, 'pdf', { steps: reportSteps });
          } catch (error) {
            addNotification({
              type: 'error',
              title: 'Download failed',
              message: 'Failed to download report. Please try again.',
            });
          }
          break;

        case 'download_summary':
          try {
            // Fetch session data from API
            const summaryResponse = await getAgentSession(product.id);
            const { session: summarySessionData, steps: summarySteps } = summaryResponse.data;

            // Load session data into store
            await handleContinueMultiVendorAssessment({
              session: summarySessionData as MultiVendorAgentSession,
              steps: summarySteps,
            });

            // Generate and download summary with steps (format is 'summary_pdf' not 'summary')
            await generateAndDownloadSessionReport(product.id, 'summary_pdf', { steps: summarySteps });
          } catch (error) {
            addNotification({
              type: 'error',
              title: 'Download failed',
              message: 'Failed to download summary. Please try again.',
            });
          }
          break;

        case 'version_history':
          openVersionHistoryDrawer({
            vendorName: data?.vendor_name || '',
            productName: product.product_name,
            vendorLogoUrl: data?.vendor_logo_url || null,
          });
          break;

        default:
          break;
      }
    },
    [navigate, openVersionHistoryDrawer, data],
  );

  const breadcrumbItems = [
    {
      title: 'Third-Party Risk',
      href: `/${ROUTES.THIRD_PARTY_VENDORS_V2}`,
    },
    {
      title: decodedVendorName || 'Vendor Details',
      href: '#',
    },
  ];

  // Loading state
  if (isLoading) {
    return <VendorDetailSkeleton />;
  }

  // Error state
  if (isError || !data) {
    return (
      <VendorDetailError
        type={getErrorType(error)}
        vendorName={decodedVendorName}
        onRetry={() => refetch()}
        onGoBack={() => navigate(-1)}
      />
    );
  }

  // Success - render content with breadcrumb
  return (
    <div className="w-full h-full flex flex-col">
      {/* Top Bar: Breadcrumb */}
      <div className="flex items-center pb-4 min-h-[44px] max-h-[44px]">
        <ZBreadCrumbs items={breadcrumbItems} />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-auto bg-[#F8FAFC] p-4 rounded-lg">
        <VendorDetailPageContent
          vendorData={data}
          memberIdNameMapping={teamsData?.memberIdNameMapping}
          onNewAssessment={handleNewAssessment}
          onViewAssessment={(productId) => navigate(`/agent/${productId}`)}
          onProductAction={handleProductAction}
        />
      </div>

      {/* Upload Dialog - New Assessment Modal */}
      <UploadDialog data={uploadDialogState} setIsOpen={setUploadDialogState} />

      {/* Version History Drawer */}
      <VersionHistoryDrawer />
    </div>
  );
}
